import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconAIMagic } from '@components/icons/ai-magic';
import { IconPlan } from '@components/icons/icon-plan';
import { IconIntegration } from '@components/icons/integration';
import { IconPerson } from '@components/icons/person';
import { IconPortal } from '@components/icons/portal';
import { IconSignout } from '@components/icons/signout';
import { Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { fullAppReload } from '@utils/browser';

import { setNeedsOrgSelection, useCurrentUser, useLogout } from 'api/authentication/hooks';
import { routePaths } from 'ui/authenticated/navigation';

export const useForcedExpiredModal = () => {
  const currentUser = useCurrentUser();
  const dateToClose = currentUser?.plan?.dateToClose ?? '';
  const showModal = useModal(TrialExpiredModal, {
    variant: 'LARGE',
    backgroundColor: 'gradient',
    hasBlurredAndForcedBackdrop: true,
  });
  const { pathname } = useLocation();
  const isOnPlanPage = pathname.includes('/plan');
  const isExpired = dateToClose && new Date(dateToClose) < new Date();
  const shouldShowModal = isExpired && !isOnPlanPage;

  useEffect(() => {
    if (shouldShowModal) {
      showModal();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
};

const TrialExpiredModal = () => {
  const { t } = useScopedTranslate(locale);
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const hasMultipleOrgs = (currentUser?.companies?.length ?? 0) > 1;
  const logout = useLogout();

  const handleSwitchOrganization = () => {
    setNeedsOrgSelection('signin');
    fullAppReload();
  };

  const features = [
    {
      icon: <IconAIMagic color="aiGradientTeal100" />,
      text: t('featureAIDriven'),
    },
    {
      icon: <IconPortal color="aiGradientTeal100" />,
      text: t('featureSelfService'),
    },
    {
      icon: <IconIntegration color="aiGradientTeal100" />,
      text: t('featureIntegrations'),
    },
    {
      icon: <IconPerson color="aiGradientTeal100" />,
      text: t('featureInsights'),
    },
  ];

  const handleSignout = () => {
    logout.mutate();
  };

  return (
    <Modal style={{ position: 'relative' }}>
      <IconContainer>
        <IconPlan size={648} rotateDegrees={45} color="uiTrans5" />
      </IconContainer>
      <Modal.Header>
        <Modal.HeaderTitle title={t('title')} />
        <Text color="textSecondary" font="regular14" text={t('description')} />
      </Modal.Header>
      <Modal.Content style={{ position: 'relative' }}>
        <Stack space={4} style={{ zIndex: 1 }}>
          <Text aiGradient text={t('upgradeNow')} font="bold18" />
          {features.map((feature) => (
            <Stack orientation="horizontal" space={4} align="center" key={feature.text}>
              {feature.icon}
              <Text text={feature.text} font="semibold14" />
            </Stack>
          ))}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', zIndex: 1, position: 'relative' }}
        >
          <Stack space={4} orientation="horizontal" align="center">
            <Button
              iconLeft={<IconSignout />}
              variant="secondary"
              text={t('signout')}
              onClick={handleSignout}
            />
            {hasMultipleOrgs && (
              <Button
                variant="secondary"
                text={t('switchOrganization')}
                onClick={handleSwitchOrganization}
              />
            )}
            <Button
              text={t('viewPlans')}
              iconLeft={<IconAIMagic />}
              variant="primary-blue"
              onClick={() => navigate(routePaths.subscriptionPlans)}
            />
          </Stack>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    title: 'Your 14 day free trial has ended',
    description:
      'Your journeys are currently paused - but no worries, all your work is safe and waiting for you',
    upgradeNow: 'Upgrade now to:',
    featureAIDriven: 'Build powerful AI-driven workflows that deliver real impact',
    featureSelfService: 'Enable teams with easy-to-use self-service portals for daily tasks',
    featureIntegrations: 'Connect with the tools you already use — effortlessly',
    featureInsights: 'Gain full visibility and insights across every journey',
    viewPlans: 'View plans',
    signout: 'Sign out',
    switchOrganization: 'Switch organization',
  },
};

const IconContainer = styled.div({
  position: 'absolute',
  left: '30%',
  top: '-50%',
  zIndex: 0,
});
